import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from "@/components/ui/use-toast";
import { PurchaseOrder, POFormData, POStatus, ProcurementKPIs } from '@/types/procurement';

export const usePurchaseOrders = () => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all purchase orders with related data
  const fetchPurchaseOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('purchase_order')
        .select(`
          *,
          supplier:suppliers(id, name, code, email, phone),
          purchase_requisition(id, code),
          po_lines:po_line(
            *,
            item:inventory_items(id, code, name, unit)
          ),
          approver:users!purchase_order_approved_by_fkey(id, email, full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform the data to match our interface
      const transformedData = data?.map(po => ({
        ...po,
        total_amount: po.total || 0,
        po_lines: po.po_lines || [],
      })) || [];

      setPurchaseOrders(transformedData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch purchase orders';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Get PO statistics/KPIs
  const getKPIs = useCallback((): ProcurementKPIs => {
    return {
      draft: purchaseOrders.filter(po => po.status === 'DRAFT').length,
      submitted: purchaseOrders.filter(po => po.status === 'SENT').length,
      ordered: purchaseOrders.filter(po => po.status === 'PARTIAL').length,
      closed: purchaseOrders.filter(po => po.status === 'RECEIVED' || po.status === 'CLOSED').length,
    };
  }, [purchaseOrders]);

  // Create new purchase order
  const createPurchaseOrder = useCallback(async (poData: POFormData): Promise<string | null> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Generate PO code
      const { data: codeData, error: codeError } = await supabase
        .rpc('generate_procurement_code', { table_type: 'PO' });

      if (codeError) throw codeError;

      // Calculate line totals and PO totals
      const linesWithTotals = poData.lines.map(line => ({
        ...line,
        tax_rate: line.tax_rate || 0,
        line_total: line.qty_ordered * line.unit_price * (1 + (line.tax_rate || 0) / 100)
      }));

      const subtotal = linesWithTotals.reduce((sum, line) => sum + (line.qty_ordered * line.unit_price), 0);
      const tax_amount = linesWithTotals.reduce((sum, line) => sum + (line.qty_ordered * line.unit_price * (line.tax_rate || 0) / 100), 0);
      const total = subtotal + tax_amount;

      // Insert purchase order
      const { data: po, error: poError } = await supabase
        .from('purchase_order')
        .insert([{
          code: codeData,
          supplier_id: poData.supplier_id,
          pr_id: poData.pr_id || null,
          status: 'DRAFT',
          currency: poData.currency,
          order_date: new Date().toISOString().split('T')[0],
          expected_date: poData.expected_date || null,
          subtotal,
          tax_percentage: poData.tax_percentage,
          tax_amount,
          total,
        }])
        .select('id')
        .single();

      if (poError) throw poError;

      // Insert PO lines if any
      if (poData.lines.length > 0) {
        const poLines = linesWithTotals.map(line => ({
          po_id: po.id,
          item_id: line.item_id,
          qty_ordered: line.qty_ordered,
          uom: line.uom,
          unit_price: line.unit_price,
          tax_rate: line.tax_rate || 0,
          // line_total is a generated column, don't include it
        }));

        const { error: linesError } = await supabase
          .from('po_line')
          .insert(poLines);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: `Purchase order ${codeData} created successfully`,
      });

      // Refresh the list
      await fetchPurchaseOrders();
      return po.id;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create purchase order';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchPurchaseOrders]);

  // Update purchase order
  const updatePurchaseOrder = useCallback(async (id: string, poData: POFormData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check if PO exists and is in DRAFT status
      const { data: existingPO, error: checkError } = await supabase
        .from('purchase_order')
        .select('status, code')
        .eq('id', id)
        .single();

      if (checkError) throw checkError;

      if (existingPO.status !== 'DRAFT') {
        throw new Error('Only draft purchase orders can be modified');
      }

      // Calculate line totals and PO totals
      const linesWithTotals = poData.lines.map(line => ({
        ...line,
        tax_rate: line.tax_rate || 0,
        line_total: line.qty_ordered * line.unit_price * (1 + (line.tax_rate || 0) / 100)
      }));

      const subtotal = linesWithTotals.reduce((sum, line) => sum + (line.qty_ordered * line.unit_price), 0);
      const tax_amount = linesWithTotals.reduce((sum, line) => sum + (line.qty_ordered * line.unit_price * (line.tax_rate || 0) / 100), 0);
      const total = subtotal + tax_amount;

      // Update purchase order
      const { error: poError } = await supabase
        .from('purchase_order')
        .update({
          supplier_id: poData.supplier_id,
          currency: poData.currency,
          expected_date: poData.expected_date || null,
          subtotal,
          tax_percentage: poData.tax_percentage,
          tax_amount,
          total,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (poError) throw poError;

      // Delete existing lines and insert new ones
      const { error: deleteError } = await supabase
        .from('po_line')
        .delete()
        .eq('po_id', id);

      if (deleteError) throw deleteError;

      // Insert new PO lines
      if (poData.lines.length > 0) {
        const poLines = linesWithTotals.map(line => ({
          po_id: id,
          item_id: line.item_id,
          qty_ordered: line.qty_ordered,
          uom: line.uom,
          unit_price: line.unit_price,
          tax_rate: line.tax_rate || 0,
          line_total: line.line_total,
        }));

        const { error: linesError } = await supabase
          .from('po_line')
          .insert(poLines);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: `Purchase order ${existingPO.code} updated successfully`,
      });

      // Refresh the list
      await fetchPurchaseOrders();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update purchase order';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchPurchaseOrders]);

  // Send purchase order (DRAFT → SENT)
  const sendPurchaseOrder = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('purchase_order')
        .update({
          status: 'SENT',
          sent_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('status', 'DRAFT'); // Only allow sending from DRAFT status

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Purchase order sent to supplier successfully',
      });

      // Refresh the list
      await fetchPurchaseOrders();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send purchase order';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchPurchaseOrders]);

  // Delete purchase order (only if status is DRAFT)
  const deletePurchaseOrder = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check if PO is in DRAFT status
      const { data: po, error: checkError } = await supabase
        .from('purchase_order')
        .select('status, code')
        .eq('id', id)
        .single();

      if (checkError) throw checkError;

      if (po.status !== 'DRAFT') {
        throw new Error('Only draft purchase orders can be deleted');
      }

      // Delete the PO (lines will be deleted automatically due to CASCADE)
      const { error } = await supabase
        .from('purchase_order')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Purchase order ${po.code} deleted successfully`,
      });

      // Refresh the list
      await fetchPurchaseOrders();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete purchase order';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchPurchaseOrders]);

  // Get single purchase order by ID
  const getPurchaseOrder = useCallback(async (id: string): Promise<PurchaseOrder | null> => {
    try {
      const { data, error } = await supabase
        .from('purchase_order')
        .select(`
          *,
          supplier:suppliers(id, name, code, email, phone),
          purchase_requisition(id, code),
          po_lines:po_line(
            *,
            item:inventory_items(id, code, name, unit)
          ),
          approver:users!purchase_order_approved_by_fkey(id, email, full_name)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Transform the data
      return {
        ...data,
        total_amount: data.total || 0,
        po_lines: data.po_lines || [],
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch purchase order';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    }
  }, []);

  // Load purchase orders on hook initialization
  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  return {
    purchaseOrders,
    loading,
    error,
    getKPIs,
    fetchPurchaseOrders,
    createPurchaseOrder,
    updatePurchaseOrder,
    sendPurchaseOrder,
    deletePurchaseOrder,
    getPurchaseOrder,
  };
};
